import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'

const MAX_HEIGHT = 1000

interface ResizableChartProps {
  storageKey: string
  label: string
  defaultHeight: number
  minHeight: number
  collapsed: boolean
  height?: number
  onHeightChange?: (height: number) => void
  children: (height: number) => ReactNode
}

function clampHeight(height: number, minHeight: number) {
  return Math.round(Math.min(MAX_HEIGHT, Math.max(minHeight, height)))
}

function readHeight(storageKey: string, defaultHeight: number, minHeight: number) {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(storageKey) || 'null')
    if (typeof saved === 'number' && Number.isFinite(saved)) return clampHeight(saved, minHeight)
  } catch { /* Browser preferences are optional. */ }
  return defaultHeight
}

/** Each pane keeps its size across tickers, intervals, view modes, and reloads. */
export function ResizableChart(props: ResizableChartProps) {
  return <ResizableChartPane key={props.storageKey} {...props} />
}

function ResizableChartPane({ storageKey, label, defaultHeight, minHeight, collapsed, height: controlledHeight, onHeightChange, children }: ResizableChartProps) {
  const [localHeight, setLocalHeight] = useState(() => readHeight(storageKey, defaultHeight, minHeight))
  const height = controlledHeight === undefined ? localHeight : clampHeight(controlledHeight, minHeight)
  const setHeight = onHeightChange || setLocalHeight
  const saveHeight = (value: number) => {
    setHeight(value)
    if (onHeightChange) return
    try { localStorage.setItem(storageKey, JSON.stringify(value)) } catch { /* Keep resizing without storage. */ }
  }

  return (
    <div className="relative min-w-0" data-resizable-chart={label}>
      {children(height)}
      {!collapsed && <ChartHeightHandle label={label} height={height} minHeight={minHeight}
        defaultHeight={defaultHeight} onChange={setHeight} onCommit={saveHeight} />}
    </div>
  )
}

function ChartHeightHandle({ label, height, minHeight, defaultHeight, onChange, onCommit }: {
  label: string
  height: number
  minHeight: number
  defaultHeight: number
  onChange: (height: number) => void
  onCommit: (height: number) => void
}) {
  const dragRef = useRef<{
    pointerId: number
    startY: number
    startHeight: number
    height: number
    restore: () => void
  } | null>(null)
  const frameRef = useRef<number | null>(null)
  const [dragging, setDragging] = useState(false)

  useEffect(() => () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    const drag = dragRef.current
    dragRef.current = null
    drag?.restore()
  }, [])

  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    drag.height = clampHeight(drag.startHeight + event.clientY - drag.startY, minHeight)
    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null
        if (dragRef.current) onChange(dragRef.current.height)
      })
    }
  }
  const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    // Capture the release position even when it precedes the next animation frame.
    if (event.type === 'pointerup') {
      drag.height = clampHeight(drag.startHeight + event.clientY - drag.startY, minHeight)
    }
    dragRef.current = null
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    frameRef.current = null
    drag.restore()
    setDragging(false)
    onCommit(drag.height)
  }

  return (
    <div
      role="separator"
      aria-label={`Resize ${label} height`}
      aria-orientation="horizontal"
      aria-valuemin={minHeight}
      aria-valuemax={MAX_HEIGHT}
      aria-valuenow={height}
      aria-valuetext={`${height} pixels`}
      tabIndex={0}
      title="Drag to resize chart height · Double-click to reset"
      className={`group relative z-20 flex h-2.5 w-full cursor-row-resize touch-none select-none items-center justify-center rounded outline-none hover:bg-teal-500/15 focus-visible:bg-teal-500/20 ${dragging ? 'bg-teal-500/20' : ''}`}
      onPointerDown={(event) => {
        if (event.button !== 0 || !event.isPrimary || dragRef.current) return
        event.preventDefault()
        event.stopPropagation()
        const target = event.currentTarget
        const pointerId = event.pointerId
        target.focus({ preventScroll: true })
        target.setPointerCapture(pointerId)
        const { cursor, userSelect } = document.body.style
        document.body.style.cursor = 'row-resize'
        document.body.style.userSelect = 'none'
        dragRef.current = {
          pointerId, startY: event.clientY, startHeight: height, height,
          restore: () => {
            document.body.style.cursor = cursor
            document.body.style.userSelect = userSelect
            if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId)
          },
        }
        setDragging(true)
      }}
      onPointerMove={moveDrag}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onLostPointerCapture={finishDrag}
      onDoubleClick={(event) => { event.stopPropagation(); onCommit(defaultHeight) }}
      onKeyDown={(event) => {
        if (dragRef.current || !['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return
        event.preventDefault()
        event.stopPropagation()
        const step = event.shiftKey ? 50 : 10
        const value = event.key === 'Home' ? minHeight : event.key === 'End' ? MAX_HEIGHT
          : height + (event.key === 'ArrowDown' ? step : -step)
        onCommit(clampHeight(value, minHeight))
      }}
    >
      <span className={`h-0.5 w-10 rounded-full group-hover:bg-teal-400 group-focus-visible:bg-teal-400 ${dragging ? 'bg-teal-400' : 'bg-slate-600/70'}`} />
    </div>
  )
}
